import { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  sliceKey,
  reducer,
  myautoiqNurturingJourneySettingsActions,
} from 'app/pages/MyautoiqNurturingJourneySettingsComponent/slice';
import { watchMyautoiqNurturingJourneySettingsSaga } from 'app/pages/MyautoiqNurturingJourneySettingsComponent/saga';
import { selectMyautoiqNurturingJourneySettings } from 'app/pages/MyautoiqNurturingJourneySettingsComponent/selectors';
import { useNurturingJourneySettings } from 'app/pages/NurturingJourneySettingsComponent/hooks/useNurturingJourneySettings';
import { NurturingSegmentContent } from 'app/pages/NurturingJourneySettingsComponent/components/NurturingSegmentContent';
import {
  saveMyautoiqNurturingJourneySettings,
  initializeMyautoiqNurturingJourneySettings,
} from 'app/pages/MyautoiqNurturingJourneySettingsComponent/data/myautoiqNurturingJourneySettingsDataService';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { NurturingJourneyControls } from 'app/components/ReusableUIComponents/NurturingJourneyControls';
import { NurturingJourneyHowToUseInfo } from 'app/components/ReusableUIComponents/NurturingJourneyHowToUseInfo';
import {
  UNSOLD_STAGE_KEYS,
  UNSOLD_STAGE_LABELS,
  SOLD_STAGE_KEYS,
  SOLD_STAGE_LABELS,
} from 'app/pages/NurturingJourneySettingsComponent/data/constants';

import {
  AccordionComponent,
  AccordionSummaryComponent,
  AccordionDetailsComponent,
} from 'app/components/StyledComponents';

import {
  Typography,
  Box,
  Button,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

interface MyautoiqNurturingJourneySettingsComponentProps {
  manufacturerId: string;
}

export const MyautoiqNurturingJourneySettingsComponent = ({
  manufacturerId,
}: MyautoiqNurturingJourneySettingsComponentProps) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: watchMyautoiqNurturingJourneySettingsSaga });

  const dispatch = useDispatch();
  const nurturingJourneyState = useSelector(selectMyautoiqNurturingJourneySettings);
  const { data, loading, error } = nurturingJourneyState;

  const {
    segments,
    isDirty,
    resetToOriginal,
    reorderSteps,
    updateStep,
    updateMaxRepeat,
    addStep,
    deleteStep,
  } = useNurturingJourneySettings(data);

  const notifyStepZeroReorderBlocked = useCallback(() => {
    dispatch(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'warning',
        message:
          'You cannot change the order of Step 0. It must stay in the first position.',
      }),
    );
  }, [dispatch]);

  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0);
  const [recordType, setRecordType] = useState<'unsold' | 'sold'>('unsold');
  const [unsoldStageTab, setUnsoldStageTab] = useState(0);
  const [soldStageTab, setSoldStageTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const currentSegment = segments[selectedSegmentIndex];
  const unsoldStages = currentSegment?.nurturing_journey_config?.record_types?.unsold?.nurturing_stages ?? {};
  const soldStages = currentSegment?.nurturing_journey_config?.record_types?.sold?.nurturing_stages ?? {};
  const unsoldStageKeys = UNSOLD_STAGE_KEYS.filter(k => unsoldStages[k]);
  const soldStageKeys = SOLD_STAGE_KEYS.filter(k => soldStages[k]);

  // Check if response indicates an error (even with 200 status code)
  const hasError = useMemo(() => {
    if (!data) return false;
    const statusText = data?.statusText;
    const statusCode = data?.statusCode ?? data?.myAutoIQ_status_code ?? data?.errorCode;
    
    // If statusText is not "success" or status code is >= 400, it's an error
    return (statusText && statusText !== 'success') || (typeof statusCode === 'number' && statusCode >= 400);
  }, [data]);

  useEffect(() => {
    if (segments.length > 0 && selectedSegmentIndex >= segments.length) {
      setSelectedSegmentIndex(0);
    }
  }, [segments.length, selectedSegmentIndex]);

  useEffect(() => {
    setUnsoldStageTab(0);
    setSoldStageTab(0);
  }, [selectedSegmentIndex, recordType]);

  useEffect(() => {
    if (manufacturerId) {
      dispatch(
        myautoiqNurturingJourneySettingsActions.requestMyautoiqNurturingJourneySettings({
          manufacturerId: manufacturerId,
        }),
      );
    }
  }, [manufacturerId, dispatch]);

  const hasData = data != null;

  const handleSave = async () => {
    if (!manufacturerId || !isDirty || saving) return;
    setSaving(true);
    try {
      await saveMyautoiqNurturingJourneySettings({
        manufacturerId: manufacturerId,
        payload: { manufacturer_id: manufacturerId, segments },
      });
      dispatch(notificationAlertActions.addNotification({
        open: true,
        severity: 'success',
        message: 'Myautoiq nurturing journey settings saved successfully',
      }));
      dispatch(
        myautoiqNurturingJourneySettingsActions.requestMyautoiqNurturingJourneySettings({
          manufacturerId: manufacturerId,
        }),
      );
    } catch (err: any) {
      dispatch(notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: err?.message || 'Failed to save myautoiq nurturing journey settings',
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    resetToOriginal();
  };

  const handleInitialize = async () => {
    if (!manufacturerId || initializing) return;
    setInitializing(true);
    try {
      const response = await initializeMyautoiqNurturingJourneySettings({
        manufacturerId,
      });
      
      // Check if initialization was successful
      const statusText = response?.statusText;
      if (statusText === 'success') {
        dispatch(notificationAlertActions.addNotification({
          open: true,
          severity: 'success',
          message: 'Nurturing journey settings initialized successfully',
        }));
        // Refetch data after successful initialization
        dispatch(
          myautoiqNurturingJourneySettingsActions.requestMyautoiqNurturingJourneySettings({
            manufacturerId: manufacturerId,
          }),
        );
      } else {
        throw new Error(response?.message || 'Failed to initialize settings');
      }
    } catch (error: any) {
      dispatch(notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: error?.message || 'Failed to initialize nurturing journey settings',
      }));
    } finally {
      setInitializing(false);
    }
  };

  // Show initialize button when:
  // 1. Not loading AND manufacturer is selected
  // 2. AND (there's an error OR no data OR no segments OR data is empty/null)
  const noValidData = !data || !hasData || segments.length === 0;
  const showInitializeButton = !loading && manufacturerId && (hasError || noValidData);

  return (
    <AccordionComponent
      elevation={0}
      sx={{
        '&::before': { display: 'none' },
        padding: '16px',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '5px !important',
        marginBottom: '16px',
      }}
    >
      <AccordionSummaryComponent
        expandIcon={<ExpandMoreIcon />}
        aria-controls="myautoiq-nurturing-journey-settings-content"
        id="myautoiq-nurturing-journey-settings-header"
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" component="div" sx={{ mb: 0 }}>
            Nurturing Journey Settings
          </Typography>
          <NurturingJourneyHowToUseInfo />
        </Box>
      </AccordionSummaryComponent>
      <AccordionDetailsComponent>
        {loading && (
          <Typography color="textSecondary">Loading...</Typography>
        )}
        {!manufacturerId && (
          <Typography color="textSecondary">
            Select a manufacturer to load myautoiq nurturing journey settings.
          </Typography>
        )}

        {showInitializeButton && (
          <>
            {hasError && data?.message && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {data.message}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowIcon />}
                onClick={handleInitialize}
                disabled={initializing}
              >
                {initializing ? 'Initializing...' : 'Initialize Nurturing Journey Settings'}
              </Button>
            </Box>
            {!hasError && !error && (
              <Typography color="textSecondary" sx={{ textAlign: 'center', mb: 2 }}>
                No nurturing journey config found. Click Initialize to set up.
              </Typography>
            )}
          </>
        )}

        {!loading && manufacturerId && !showInitializeButton && segments.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {/* Sticky header: controls row + stage tabs */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper',
                mb: 1,
                boxShadow: '0 6px 16px -4px rgba(0,0,0,0.18)',
                px: 2,
                pt: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  pb: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <NurturingJourneyControls
                  segments={segments}
                  selectedSegmentIndex={selectedSegmentIndex}
                  recordType={recordType}
                  onSegmentChange={setSelectedSegmentIndex}
                  onRecordTypeChange={setRecordType}
                  labelPrefix="myautoiq-nurturing"
                />
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancel}
                    disabled={!isDirty || loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={!isDirty || loading || saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </Box>
              {/* Stage tabs */}
              {recordType === 'unsold' && unsoldStageKeys.length > 0 && (
                <Tabs
                  value={unsoldStageTab}
                  onChange={(_, v) => setUnsoldStageTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTabs-flexContainer': { justifyContent: 'flex-end' },
                  }}
                >
                  {unsoldStageKeys.map(key => (
                    <Tab key={key} label={UNSOLD_STAGE_LABELS[key] ?? key} />
                  ))}
                </Tabs>
              )}
              {recordType === 'sold' && soldStageKeys.length > 0 && (
                <Tabs
                  value={soldStageTab}
                  onChange={(_, v) => setSoldStageTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTabs-flexContainer': { justifyContent: 'flex-end' },
                  }}
                >
                  {soldStageKeys.map(key => (
                    <Tab key={key} label={SOLD_STAGE_LABELS[key] ?? key} />
                  ))}
                </Tabs>
              )}
            </Box>

            <NurturingSegmentContent
              segment={segments[selectedSegmentIndex]}
              segmentIndex={selectedSegmentIndex}
              recordType={recordType}
              unsoldStageTab={unsoldStageTab}
              soldStageTab={soldStageTab}
              unsoldStageKeys={unsoldStageKeys}
              soldStageKeys={soldStageKeys}
              onReorderSteps={(rt, stageKey, oldIndex, newIndex) =>
                reorderSteps(selectedSegmentIndex, rt, stageKey, oldIndex, newIndex)
              }
              onUpdateStep={(rt, stageKey, stepIndex, patch) =>
                updateStep(selectedSegmentIndex, rt, stageKey, stepIndex, patch)
              }
              onUpdateMaxRepeat={(rt, stageKey, value) =>
                updateMaxRepeat(selectedSegmentIndex, rt, stageKey, value)
              }
              onAddStep={(rt, stageKey, step) =>
                addStep(selectedSegmentIndex, rt, stageKey, step)
              }
              onDeleteStep={(rt, stageKey, stepIndex) =>
                deleteStep(selectedSegmentIndex, rt, stageKey, stepIndex)
              }
              onStepZeroReorderBlocked={notifyStepZeroReorderBlocked}
              disabled={loading}
            />
          </Box>
        )}
      </AccordionDetailsComponent>
    </AccordionComponent>
  );
};
